import {gql, type TypedDocumentNode} from '@apollo/client';
import {useQuery} from '@apollo/client/react';
import {useMemo} from 'react';

import type {_Array, _Object} from '../../Paramut';
import buildScheduleIndex from '../../SupportGroups/buildScheduleIndex';
import type {ScheduleIndex} from '../../SupportGroups/ScheduleIndex';
import type {ScheduleRecords} from '../../SupportGroups/ScheduleRecords';
import type {Millisecond} from '../../Time';
import getLocalTimeIfDiffers from '../../util/getLocalTimeIfDiffers';
import type {GetGroupQuery, GetGroupQueryVariables} from './useGroup.generated';

type UseGroupProps = {
  id: number;
  now: Millisecond;
};

type UseGroupResult = {
  group: Group | null | undefined;
  city: City | null | undefined;
  localTime?: string;
};

export type City<Mut extends boolean = false> = _Object<
  {
    name: string;
    timezone: string;
  },
  Mut
>;

export type Group<Mut extends boolean = false> = _Object<
  {
    id: number;
    name: string;
    street: string;
    house: string;
    comment: string | null;
    entrance: string | null;
    intercomCode: string | null;
    floor: string | null;
    apartmentOrOffice: string | null;
    schedule: ScheduleIndex | null;
    events: Meetings<Mut>;
  },
  Mut
>;

export type Meetings<Mut extends boolean = false> = _Array<Meeting<Mut>, Mut>;

export type Meeting<Mut extends boolean = false> = _Object<
  {
    id: number;
    /**
     * @example 0
     */
    sequence: number;
    /**
     * @example "2025-10-03T22:06:33.915Z"
     */
    updatedAt: string;
    /**
     * @example 6 // Saturday
     */
    day: number;
    start: _Object<
      {
        hour: number;
        minute: number;
      },
      Mut
    >;
    duration: number;
  },
  Mut
>;

export default function useGroup(props: UseGroupProps): UseGroupResult {
  const {id, now} = props;

  const {data} = useQuery(GET_GROUP, {
    variables: {id},
  });

  const city = data?.SupportGroup?.location.city;
  const {timezone} = city ?? {};
  const serverGroup = data?.SupportGroup;
  const group = useMemo<Group | undefined | null>(() => {
    if (!serverGroup) {
      return serverGroup;
    }
    const events = serverGroup.events?.docs;
    return {
      id: serverGroup.id,
      name: serverGroup.name,
      street: serverGroup.location.street,
      house: serverGroup.location.house,
      comment: serverGroup.location.comment ?? null,
      entrance: serverGroup.location.entrance ?? null,
      intercomCode: serverGroup.location.intercomCode ?? null,
      floor: serverGroup.location.floor ?? null,
      apartmentOrOffice: serverGroup.location.apartmentOrOffice ?? null,
      schedule:
        events && events.length > 0
          ? buildScheduleIndex(events as ScheduleRecords)
          : null,
      events:
        serverGroup.events?.docs.map(
          _ =>
            ({
              ..._,
              updatedAt: _.updatedAt as string,
              start: _.start as {hour: number; minute: number},
            }) satisfies Meeting,
        ) ?? [],
    } satisfies Group;
  }, [serverGroup]);

  const localTime = useMemo(
    () => getLocalTimeIfDiffers(now, timezone),
    [now, timezone],
  );

  return {city, group, localTime};
}

const GET_GROUP: TypedDocumentNode<GetGroupQuery, GetGroupQueryVariables> = gql`
  query GetGroup($id: Int!) {
    SupportGroup(id: $id) {
      id
      name
      location {
        id
        city {
          id
          name
          timezone
        }
        street
        house
        comment
        entrance
        intercomCode
        floor
        apartmentOrOffice
      }
      events(limit: 0) {
        docs {
          id
          sequence
          day
          start {
            hour
            minute
          }
          duration
          updatedAt
        }
      }
    }
  }
`;
